import { ReloadOutlined } from '@ant-design/icons';
import { Button, Divider, Space, Switch } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import LanguageButtons from '../components/language-buttons';
import ProductAdd from '../components/product/product-add';
import Table from '../components/table';
import {
  getDataSourceForProductsTable,
  getProductsTableColumns,
  type ProductTableColumn,
} from '../fragments/products-table-columns';
import { getUpdatedAtMessage } from '../fragments/updated-at-message';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { productsLoadAll } from '../reducers/products/reducer';
import { SPACING } from '../theme/tokens';

function ProductsContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const productsState = useSelector((s: RootState) => s.Products);
  const languageState = useSelector((s: RootState) => s.Language);
  const configState = useSelector((s: RootState) => s.Config);
  const currencyState = useSelector((s: RootState) => s.Currency);
  const featureFlagsState = useSelector((s: RootState) => s.FeatureFlags);
  const warehouseState = useSelector((s: RootState) => s.Warehouse);

  const [isActiveProducts, setIsActiveProducts] = useState<boolean>(true);

  const dataSource = useMemo<ProductTableColumn[]>(
    () =>
      getDataSourceForProductsTable({
        Products: productsState,
        Language: languageState,
      }),
    [productsState, languageState],
  );

  const columns = useMemo<ColumnsType<object>>(
    () =>
      getProductsTableColumns({
        dataSource,
        Config: configState,
        Currency: currencyState,
        Language: languageState,
        FeatureFlags: featureFlagsState,
        Warehouse: warehouseState,
      }),
    [
      dataSource,
      configState,
      currencyState,
      languageState,
      featureFlagsState,
      warehouseState,
    ],
  );

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <Space
        size={SPACING.lg}
        align="center"
        separator={<Divider orientation="vertical" />}
      >
        <Switch
          checkedChildren="Active products"
          unCheckedChildren="Disabled products"
          checked={isActiveProducts}
          onChange={(checked: boolean): void => {
            setIsActiveProducts(checked);
          }}
        />

        <Button
          type={!productsState.products ? 'primary' : 'default'}
          onClick={(): void => {
            dispatch(productsLoadAll(isActiveProducts));
          }}
          disabled={productsState.inProcess}
        >
          Reload all <ReloadOutlined />
        </Button>

        {getUpdatedAtMessage(
          productsState.updatedAt !== undefined,
          productsState.updatedAt,
        )}
      </Space>

      <LanguageButtons />

      <ProductAdd />

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={productsState.inProcess && !productsState.products}
        emptyText="No products found"
      />
    </Space>
  );
}

export default ProductsContainer;
